import { analytics } from '../firebase/config';
import { logEvent, setAnalyticsCollectionEnabled } from 'firebase/analytics';

class AnalyticsManager {
  constructor() {
    this.isEnabled = false;
    this.pendingEvents = [];
    this.initializeFromStorage();
  }

  initializeFromStorage() {
    try {
      const cookieConsent = localStorage.getItem('cookieConsent');
      if (cookieConsent) {
        const consent = JSON.parse(cookieConsent);
        this.setAnalyticsEnabled(consent.analytics || false);
      }
    } catch (error) {
      console.error('Error reading cookie consent from storage:', error);
    }
  }

  setAnalyticsEnabled(enabled) {
    this.isEnabled = enabled;
    
    if (analytics) {
      setAnalyticsCollectionEnabled(analytics, enabled);
      
      // If analytics is now enabled, send any pending events
      if (enabled && this.pendingEvents.length > 0) {
        this.pendingEvents.forEach(({ eventName, eventParams }) => {
          this.trackEvent(eventName, eventParams);
        });
        this.pendingEvents = [];
      }
    }
  }

  trackEvent(eventName, eventParams = {}) {
    if (!analytics) {
      console.warn('Firebase Analytics not initialized');
      return;
    }

    if (!this.isEnabled) {
      // Store event for later if analytics gets enabled
      this.pendingEvents.push({ eventName, eventParams });
      return;
    }

    try {
      logEvent(analytics, eventName, eventParams);
    } catch (error) {
      console.error('Error tracking analytics event:', error);
    }
  }

  // Common tracking methods
  trackPageView(pageName, pageTitle = '') {
    this.trackEvent('page_view', {
      page_title: pageTitle,
      page_location: window.location.href,
      page_path: window.location.pathname,
      page_name: pageName
    });
  }

  trackSalonView(salonId, salonName = '') {
    this.trackEvent('view_salon', {
      salon_id: salonId,
      salon_name: salonName,
      page_location: window.location.href
    });
  }

  trackBookingAttempt(salonId, serviceType = '') {
    this.trackEvent('begin_booking', {
      salon_id: salonId,
      service_type: serviceType,
      page_location: window.location.href
    });
  }

  trackBookingComplete(salonId, serviceType = '', value = 0) {
    this.trackEvent('booking_complete', {
      salon_id: salonId,
      service_type: serviceType,
      value: value,
      currency: 'EUR',
      page_location: window.location.href
    });
  }

  trackSearch(searchTerm, category = '', location = '') {
    this.trackEvent('search', {
      search_term: searchTerm,
      category: category,
      location: location,
      page_location: window.location.href
    });
  }

  trackSignUp(method = 'email', userType = '') {
    this.trackEvent('sign_up', {
      method: method,
      user_type: userType,
      page_location: window.location.href
    });
  }

  trackLogin(method = 'email', userType = '') {
    this.trackEvent('login', {
      method: method,
      user_type: userType,
      page_location: window.location.href
    });
  }

  trackSubscription(planType = '', value = 0) {
    this.trackEvent('purchase', {
      transaction_id: Date.now().toString(),
      value: value,
      currency: 'EUR',
      items: [{
        item_id: planType,
        item_name: `BookingBarber ${planType} Plan`,
        category: 'subscription',
        quantity: 1,
        price: value
      }]
    });
  }

  trackCookieConsent(analyticsEnabled) {
    this.trackEvent('cookie_consent', {
      analytics_enabled: analyticsEnabled,
      page_location: window.location.href
    });
  }
}

// Create singleton instance
const analyticsManager = new AnalyticsManager();

export default analyticsManager;

// Export individual methods for convenience
export const {
  setAnalyticsEnabled,
  trackEvent,
  trackPageView,
  trackSalonView,
  trackBookingAttempt,
  trackBookingComplete,
  trackSearch,
  trackSignUp,
  trackLogin,
  trackSubscription,
  trackCookieConsent
} = analyticsManager;